import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent as ReactKeyboardEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    BodyScrollEndEvent,
    ColDef,
    GetRowIdParams,
    ICellRendererParams,
    SortChangedEvent
} from 'ag-grid-community';
import _ from 'lodash';
import {AppGrid, GridLoading} from 'components/common/ag-grid/AppGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {
    ACTION_BUTTONS,
    actionsColumn,
    configurationGridHeight,
    gridColumn,
    isAgGridInConfigurationEnabled,
    ROW_HEIGHT,
    RowActions,
    tabIsNotTheGrids,
    tabOutOfTheHeaderIsNotTheGrids,
    walkRowControls
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {isLastRowRendered, useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import {Button, ButtonType} from 'components/common/Button.component';
import {SegmentedControl} from 'components/common/SegmentedControl.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {formatDate} from 'utils/valueFormatter.function';
import {formatNameWithEmail} from 'utils/userFormatter.function';
import type {Translate} from 'types/myTasks';
import {
    PersonField,
    STATUS_FILTER,
    StatusFilterValue,
    Substitute,
    SubstituteGridRow,
    SubstitutesSort
} from 'types/substitutes';
import * as reduxSelectors from '../store/application.reducers';
import SearchGroupInput from '../input/SearchGroupInput.component';
import Substitutes from './Substitutes.component';
import {
    changeActiveFilter,
    changeSearchValue,
    changeSort,
    deleteSubstitute,
    getSubstitutes
} from './substitutes.action';

// the page, as its class names and ids spell it
const PAGE = 'substitutes';

const TODAY = new Date().setHours(0, 0, 0, 0);
const NOT_ASSIGNED = 'N/A';

// what a date column shows when there is no date to show
const NO_DATE = '-';

const FIRST_PAGE = 1;
const SEARCH_DEBOUNCE_MS = 500;

// the grid fills the page, keeping the card's own padding and the page's bottom margin below it
const GRID_HEIGHT = configurationGridHeight(PAGE);

// the columns the grid draws, in the order the classic list had them
const USER_COL = 'user';
const SUBSTITUTE_COL = 'substitute';
const NOT_BEFORE_COL = 'notBefore';
const NOT_AFTER_COL = 'notAfter';
const STATUS_COL = 'status';

// the header only ever flips between the two, never back to unsorted, as the classic list did
const SORTING_ORDER: ('asc' | 'desc')[] = ['asc', 'desc'];

/** Nothing before a start date, and active or planned once there is one. */
function computeStatus(fromDate: number | null, translate: Translate): string {
    if (fromDate === null) {
        return '';
    }
    return fromDate <= TODAY ? translate('substitutes.active') : translate('substitutes.planned');
}

function buildRowData(substitutes: Substitute[], translate: Translate): SubstituteGridRow[] {
    return substitutes.map((substitute, index) => {
        const fromDate = substitute.from ? new Date(substitute.from).setHours(0, 0, 0, 0) : null;
        const toDate = substitute.to ? new Date(substitute.to).setHours(0, 0, 0, 0) : null;

        return {
            // the same row can be listed twice over different periods, so the pair of people is
            // not unique on its own: the version and the position are what tell those rows apart
            id: `${substitute.userId}-${substitute.substituteId}-${substitute.version}-${index}`,
            index,
            user: substitute.userName,
            substitute: substitute.substituteName === NOT_ASSIGNED ? '' : substitute.substituteName,
            notBefore: formatDate(fromDate, NO_DATE),
            notAfter: toDate
                ? formatDate(toDate, NO_DATE)
                : (substitute.from ? translate('substitutes.indefinitely') : NO_DATE),
            status: computeStatus(fromDate, translate)
        };
    });
}

/**
 * The name on the first line, the email under it - as the classic list rendered both columns.
 *
 * A div, not a span: formatNameWithEmail returns a div of its own once the value carries an
 * email, and a div inside a span is markup no browser has to lay out the way it reads.
 */
const personRenderer = (field: PersonField) => (params: ICellRendererParams<SubstituteGridRow>) => (
    <div className="app-grid__person">{formatNameWithEmail(params.data?.[field])}</div>
);

/**
 * ag-grid rendering of the Configuration -> Substitutes page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Substitutes.component.
 */
export const NewSubstitutes = () => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);
    const activeFilter: StatusFilterValue = useSelector(reduxSelectors.getSubstitutesActiveFilter);
    const searchValue: string = useSelector(reduxSelectors.getSubstitutesSearchValue);
    const substitutes = useSelector(reduxSelectors.getSubstitutesForRender);
    const sort: SubstitutesSort = useSelector(reduxSelectors.getSubstitutesSorting);
    const loadingStatus = useSelector(reduxSelectors.getSubstitutesLoadingStatus);

    // typed text lives here so keystrokes never reach redux or the API
    const [searchInput, setSearchInput] = useState<string>(searchValue || '');

    // the grid owns its scrolling area, and the loader stands in its place until the first page
    // lands. Whichever of them is mounted is the element measured, which is why it arrives
    // through a callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const allSubstitutes: Substitute[] = substitutes?.allSubstitutes ?? [];
    const showActions = !!userRoles?.systemAdministrator;

    /**
     * The loaded rows, for the handlers that only look one up when they run. Reading them off a
     * ref rather than closing over them is what keeps those handlers - and with them the column
     * definitions - the same objects across a reload, so the grid does not rebuild its columns
     * (and drop a resized width) every time a page lands.
     */
    const loadedSubstitutes = useRef(allSubstitutes);
    loadedSubstitutes.current = allSubstitutes;

    /**
     * The query a debounced search should run with. The debounced call is created once, so it
     * cannot close over the filter and the sort - it reads whatever they are when it fires.
     */
    const latestQuery = useRef({activeFilter, sort});
    latestQuery.current = {activeFilter, sort};

    const debouncedSearch = useMemo(() => _.debounce((value: string) => {
        dispatch(changeSearchValue(value));
        dispatch(getSubstitutes(FIRST_PAGE, latestQuery.current.activeFilter, value, latestQuery.current.sort, true));
    }, SEARCH_DEBOUNCE_MS), [dispatch]);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    // loads on mount, and starts over whenever the company context changes
    useEffect(() => {
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, searchValue, sort));
    }, [dispatch, currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- the filter, the search term and the sort reload through their own handlers

    /**
     * Holds the paging back until the page asked for has landed. The classic list re-armed this
     * whenever it rebuilt its rows, and a render is still what re-arms it: the grid scrolling
     * does not re-render the page, so the guard stands from the request until its response.
     */
    const stopScroll = useRef(false);
    useEffect(() => {
        stopScroll.current = false;
    });

    /** Drops the pending keystroke fetch and commits whatever is typed right now. */
    const commitSearch = useCallback(() => {
        debouncedSearch.cancel();
        if (searchInput !== searchValue) {
            dispatch(changeSearchValue(searchInput));
        }
        return searchInput;
    }, [debouncedSearch, dispatch, searchInput, searchValue]);

    const search = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
        debouncedSearch(event.target.value);
    }, [debouncedSearch]);

    const clearSearch = useCallback(() => {
        debouncedSearch.cancel();
        setSearchInput('');
        dispatch(changeSearchValue(''));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, '', sort, true));
    }, [debouncedSearch, dispatch, activeFilter, sort]);

    const filter = useCallback((value: StatusFilterValue) => {
        const committed = commitSearch();
        dispatch(changeActiveFilter(value));
        dispatch(getSubstitutes(FIRST_PAGE, value, committed, sort));
    }, [commitSearch, dispatch, sort]);

    const resetFilterToAll = useCallback(() => {
        dispatch(changeActiveFilter(STATUS_FILTER.ALL));
    }, [dispatch]);

    const closeSubstitutePopUp = useCallback(() => {
        debouncedSearch.cancel();
        setSearchInput('');
        dispatch(changeSearchValue(''));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, '', sort, true));
    }, [debouncedSearch, dispatch, activeFilter, sort]);

    const deleteRow = useCallback((index: number) => {
        const row = loadedSubstitutes.current[index];
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteSubstitute.title'),
            description: translate('substitutes.confirmDelete', row.substituteName, row.userName),
            confirmButtonText: 'popUp.remove',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal?.(null);
                // deleteSubstitute refetches the list once the delete resolves
                dispatch(deleteSubstitute(index));
            }
        });
    }, [toggleModal, translate, dispatch]);

    const editRow = useCallback((index: number) => {
        toggleModal?.(MODAL_ACTIONS.EDIT_SUBSTITUTE, {
            edit: loadedSubstitutes.current[index],
            saveCloseCallback: resetFilterToAll,
            closeCallback: closeSubstitutePopUp
        });
    }, [toggleModal, resetFilterToAll, closeSubstitutePopUp]);

    const openAddSubstitutePopUp = useCallback(() => {
        toggleModal?.(MODAL_ACTIONS.ADD_SUBSTITUTE, {
            saveCloseCallback: resetFilterToAll,
            closeCallback: closeSubstitutePopUp
        });
    }, [toggleModal, resetFilterToAll, closeSubstitutePopUp]);

    /**
     * The next page is asked for once the grid is scrolled to its last row. ag-grid virtualises
     * the rows, so there is no scrolling element of our own to watch for the bottom.
     */
    const onBodyScrollEnd = useCallback((event: BodyScrollEndEvent<SubstituteGridRow>) => {
        if (!isLastRowRendered(event.api) || stopScroll.current) {
            return;
        }
        const {records, page} = substitutes;
        if (allSubstitutes.length > 0 && allSubstitutes.length < records) {
            stopScroll.current = true;
            dispatch(getSubstitutes(page + 1, activeFilter, searchValue, sort, true));
        }
    }, [dispatch, substitutes, allSubstitutes.length, activeFilter, searchValue, sort]);

    /**
     * The endpoint sorts, not the grid, so a header click reloads the first page. The grid keeps
     * the arrow, and redux keeps the field it stands for - the column defs below hand it back,
     * so the two never drift apart.
     */
    const onSortChanged = useCallback((event: SortChangedEvent<SubstituteGridRow>) => {
        const sorted = event.api.getColumnState().find(column => !!column.sort);
        const newSort: SubstitutesSort = sorted
            ? {fieldName: sorted.colId, sortOrder: sorted.sort === 'asc'}
            : {fieldName: '', sortOrder: true};

        if (newSort.fieldName === sort.fieldName && newSort.sortOrder === sort.sortOrder) {
            // the grid was only handed back the sort it already had
            return;
        }

        const committed = commitSearch();
        dispatch(changeSort(newSort));
        dispatch(getSubstitutes(FIRST_PAGE, activeFilter, committed, newSort, true));
    }, [commitSearch, dispatch, activeFilter, sort]);

    /**
     * The tab order inside the grid: the sortable headings one by one, and then the buttons in
     * the rows, two to a row.
     */
    const onGridKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
        walkRowControls(event, ACTION_BUTTONS);
    }, []);

    const rowData = useMemo(() => buildRowData(allSubstitutes, translate), [allSubstitutes, translate]);

    const columnDefs = useMemo<ColDef<SubstituteGridRow>[]>(() => {
        const sortFor = (colId: string) => sort.fieldName === colId
            ? (sort.sortOrder ? 'asc' as const : 'desc' as const)
            : null;

        const sortableColumn = (colId: string): Partial<ColDef<SubstituteGridRow>> => ({
            sortable: true,
            sortingOrder: SORTING_ORDER,
            sort: sortFor(colId),
            // the endpoint has already ordered the rows, and only the loaded pages are here
            // anyway - so the grid draws the arrow and leaves the order alone
            comparator: () => 0
        });

        const column = (colId: string, headerKey: string, flex: number, extra: Partial<ColDef<SubstituteGridRow>> = {}) =>
            gridColumn<SubstituteGridRow>({
                colId,
                field: colId as keyof SubstituteGridRow,
                headerName: translate(headerKey),
                flex,
                minWidth: 110,
                sortable: false,
                ...extra
            });

        const personColumn = (colId: PersonField, headerKey: string): ColDef<SubstituteGridRow> =>
            column(colId, headerKey, 3, {
                ...sortableColumn(colId),
                minWidth: 160,
                cellRenderer: personRenderer(colId),
                // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked
                // for explicitly - it carries both lines, since either one can be cut
                tooltipValueGetter: params => params.data?.[colId] || undefined
            });

        // the flex weights are the proportions the classic list gave these columns with
        // col-md-3 / col-md-3 / col-md-2 / col-md-2 / col-md-1
        const columns: ColDef<SubstituteGridRow>[] = [
            personColumn(USER_COL, 'substitutes.headers.user'),
            personColumn(SUBSTITUTE_COL, 'substitutes.headers.substitute'),
            column(NOT_BEFORE_COL, 'substitutes.headers.notBefore', 2, sortableColumn(NOT_BEFORE_COL)),
            column(NOT_AFTER_COL, 'substitutes.headers.notAfter', 2, sortableColumn(NOT_AFTER_COL)),
            column(STATUS_COL, 'substitutes.headers.status', 1)
        ];

        if (showActions) {
            columns.push(actionsColumn<SubstituteGridRow>({
                headerName: translate('substitutes.headers.action'),
                // the buttons are the tab stops here, and walkRowControls is what walks them
                suppressKeyboardEvent: tabIsNotTheGrids,
                cellRenderer: (params: ICellRendererParams<SubstituteGridRow>) => {
                    const index = params.data?.index;
                    if (index === undefined) return null;
                    return (
                        <RowActions page={PAGE}
                                    rowId={String(index)}
                                    onEdit={() => editRow(index)}
                                    onDelete={() => deleteRow(index)}
                                    translate={translate}/>
                    );
                }
            }));
        }

        // the heading Tab leaves the header row from - the last one it has anything to do at
        const lastSortable = _.findLast(columns, ({sortable}) => !!sortable);
        if (lastSortable) {
            lastSortable.suppressHeaderKeyboardEvent = tabOutOfTheHeaderIsNotTheGrids;
        }

        return columns;
    }, [translate, showActions, sort.fieldName, sort.sortOrder, editRow, deleteRow]);

    // a reload keeps the rows on screen; only the very first page is waited for
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="substitutes.header"
                           infoKey="substitutes.headerInfo"
                           translate={translate}>

            <div className="d-flex justify-content-between mb-24">
                <div>
                    {showActions &&
                        <div className="btn-group">
                            <Button type={ButtonType.PRIMARY}
                                    onClick={openAddSubstitutePopUp}
                                    text={translate("substitutes.newSubstitute")}
                            />
                        </div>
                    }
                </div>

                <div className="d-flex gap-3 align-items-center">
                    <SearchGroupInput onInputChange={search}
                                      placeholder={translate("popUp.addSubstitute.name")}
                                      value={searchInput}
                                      className="ga-input__default"
                                      onClearInput={clearSearch}
                                      translate={translate}/>

                    <SegmentedControl
                        options={[
                            {
                                key: STATUS_FILTER.ALL,
                                label: translate("substitutes.all"),
                                isSelected: activeFilter === STATUS_FILTER.ALL,
                                onClick: () => filter(STATUS_FILTER.ALL)
                            },
                            {
                                key: STATUS_FILTER.CURRENT,
                                label: translate("substitutes.active"),
                                isSelected: activeFilter === STATUS_FILTER.CURRENT,
                                onClick: () => filter(STATUS_FILTER.CURRENT)
                            },
                            {
                                key: STATUS_FILTER.UPCOMING,
                                label: translate("substitutes.planned"),
                                isSelected: activeFilter === STATUS_FILTER.UPCOMING,
                                onClick: () => filter(STATUS_FILTER.UPCOMING)
                            }
                        ]}
                    />
                </div>
            </div>

            {showGrid
                ? <AppGrid<SubstituteGridRow>
                    className="new-substitutes__grid"
                    dataCy="substitutes.grid"
                    containerRef={setGridElement}
                    height={gridHeight}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={(params: GetRowIdParams<SubstituteGridRow>) => params.data.id}
                    rowHeight={ROW_HEIGHT}
                    onBodyScrollEnd={onBodyScrollEnd}
                    onSortChanged={onSortChanged}
                    onKeyDown={onGridKeyDown}
                    // one field is all the endpoint sorts by, and all onSortChanged reads back -
                    // so a shift-click must not be able to leave two columns wearing an arrow
                    suppressMultiSort={true}
                    localeText={{noRowsToShow: translate('substitutes.noMatches')}}
                />
                : <GridLoading containerRef={setGridElement} height={gridHeight}/>
            }
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Substitutes. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const SubstitutesWrapper = () => isAgGridInConfigurationEnabled() ? <NewSubstitutes/> : <Substitutes/>;

export default SubstitutesWrapper;
